import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useAPI from '../../hooks/useAPI';
import TaskCard from '../../components/tech/TaskCard';
import ProgressBar from '../../components/tech/ProgressBar';
import TechButton from '../../components/tech/TechButton';
import { getTechTasks } from '../../services/api';

/**
 * Task list — shows all tasks in optimized route order with progress.
 */
export default function TaskList() {
  const navigate = useNavigate();
  const { session } = useTechSession();
  const { t, isRTL } = useLanguage();
  const [filter, setFilter] = useState('all');

  const { data, loading, error, reload } = useAPI(
    () => getTechTasks(session?.technicianId),
    [session?.technicianId]
  );

  if (!session) {
    navigate('/tech');
    return null;
  }

  // Group tasks by site, sort by robot number within each site
  const rawTasks = data?.tasks || [];
  const tasks = [...rawTasks].sort((a, b) => (a.robot_number || 0) - (b.robot_number || 0));
  const completed = tasks.filter(t => t.status === 'completed').length;
  const filtered = filter === 'all'
    ? tasks
    : tasks.filter(t => t.status === filter);

  // Group filtered tasks by project_name
  const groupedTasks = {};
  filtered.forEach(task => {
    const site = task.project_name || 'Other';
    if (!groupedTasks[site]) groupedTasks[site] = [];
    groupedTasks[site].push(task);
  });
  // Sort robots within each group by number
  Object.values(groupedTasks).forEach(group =>
    group.sort((a, b) => (a.robot_number || 0) - (b.robot_number || 0))
  );
  const siteNames = Object.keys(groupedTasks).sort();

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.tasksToday}</h2>
        <span style={{ fontSize: 13, color: '#8b949e' }}>{data?.total || 0} {t.tasks?.toLowerCase()}</span>
      </div>

      {/* Progress */}
      <ProgressBar completed={completed} total={tasks.length} t={t} />

      {/* Filter chips */}
      <div style={styles.filterRow}>
        {['all', 'pending', 'in_progress', 'completed', 'escalated'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              ...styles.filterChip,
              ...(filter === f ? styles.filterChipActive : {}),
            }}
          >
            {f === 'all' ? 'All' : f.replace('_', ' ')}
            {f !== 'all' && ` (${tasks.filter(t => t.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Loading / Error */}
      {loading && <div style={styles.center}>{t.loading}</div>}
      {error && (
        <div style={styles.center}>
          <div style={{ color: '#d63031', marginBottom: 12 }}>{error}</div>
          <TechButton variant="secondary" onClick={reload}>{t.retry}</TechButton>
        </div>
      )}

      {/* Task cards grouped by site */}
      {!loading && filtered.length === 0 && (
        <div style={styles.center}>{t.noTasks}</div>
      )}
      {siteNames.map(site => (
        <div key={site} style={{ marginBottom: 16 }}>
          <div style={styles.siteHeader}>
            <span style={styles.siteIcon}>📍</span>
            <span style={styles.siteName}>{site}</span>
            <span style={styles.siteCount}>{groupedTasks[site].length} {t.tasks?.toLowerCase()}</span>
          </div>
          {groupedTasks[site].map((task, i) => (
            <TaskCard
              key={task.task_id}
              task={task}
              index={i}
              t={t}
              onClick={() => navigate(`/tech/task/${task.task_id}`)}
            />
          ))}
        </div>
      ))}

      {/* End Day button */}
      {completed > 0 && (
        <TechButton
          fullWidth
          variant="secondary"
          onClick={() => navigate('/tech/end-day')}
          style={{ marginTop: 16 }}
        >
          {t.endDay} ({completed}/{tasks.length})
        </TechButton>
      )}
    </div>
  );
}

const styles = {
  filterRow: {
    display: 'flex',
    gap: 6,
    marginBottom: 14,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  filterChip: {
    padding: '6px 12px',
    fontSize: 12,
    border: '1px solid #30363d',
    borderRadius: 16,
    background: 'transparent',
    color: '#8b949e',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  filterChipActive: {
    background: '#6c5ce7',
    borderColor: '#6c5ce7',
    color: '#fff',
  },
  siteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    marginBottom: 8,
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    borderRadius: 10,
    borderLeft: '3px solid #6c5ce7',
  },
  siteIcon: {
    fontSize: 16,
  },
  siteName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e6e6e6',
    flex: 1,
  },
  siteCount: {
    fontSize: 12,
    color: '#8b949e',
    background: '#30363d',
    padding: '2px 8px',
    borderRadius: 10,
  },
  center: {
    textAlign: 'center',
    padding: 32,
    color: '#8b949e',
  },
};
